import React, { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import styled from 'styled-components';
import Lenis from '@studio-freight/lenis';

// Components
import GlobalStyles from './styles/GlobalStyles';
import Cursor from './components/Cursor';
import Navigation from './components/Navigation';
import ParallaxHero from './components/ParallaxHero';
import ExperienceSection from './components/ExperienceSection';
import AnimatedServices from './components/AnimatedServices';
import FeatureSpotlight from './components/FeatureSpotlight';
import TestimonialSlider from './components/TestimonialSlider';

// Styled components
const LoadingScreen = styled(motion.div)`
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background-color: var(--color-bg-dark);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 9999;
`;

const LoadingContent = styled(motion.div)`
  text-align: center;
`;

const LogoText = styled(motion.h1)`
  font-size: 3rem;
  color: var(--color-text-primary);
  
  span {
    color: var(--color-accent-primary);
  }
`;

const LoadingBar = styled(motion.div)`
  width: 200px;
  height: 4px;
  background-color: rgba(255, 255, 255, 0.1);
  margin: 20px auto;
  border-radius: var(--radius-full);
  overflow: hidden;
  position: relative;
`;

const LoadingProgress = styled(motion.div)`
  height: 100%;
  background-color: var(--color-accent-primary);
  width: 20%;
  border-radius: var(--radius-full);
`;

const HomeContainer = styled.div`
  min-height: 100vh;
  overflow: hidden;
`;

const ContactSection = styled.section`
  padding: var(--spacing-xl) 0;
  background-color: var(--color-bg-dark);
  position: relative;
  overflow: hidden;
  
  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background: radial-gradient(circle at 30% 70%, var(--color-accent-primary) 0%, transparent 70%);
    opacity: 0.1;
    z-index: 0;
    pointer-events: none;
  }
`;

const ContentContainer = styled.div`
  max-width: 1400px;
  margin: 0 auto;
  padding: 0 var(--spacing-md);
  position: relative;
  z-index: 1;
`;

const SectionHeading = styled.div`
  text-align: center;
  margin-bottom: var(--spacing-lg);
`;

const HeadingLabel = styled(motion.div)`
  color: var(--color-accent-primary);
  text-transform: uppercase;
  letter-spacing: 3px;
  font-size: 0.875rem;
  font-weight: 500;
  margin-bottom: var(--spacing-sm);
`;

const SectionTitle = styled(motion.h2)`
  margin-bottom: var(--spacing-md);
  position: relative;
  display: inline-block;
  
  &::after {
    content: '';
    position: absolute;
    bottom: -10px;
    left: 50%;
    transform: translateX(-50%);
    width: 80px;
    height: 3px;
    background: linear-gradient(
      90deg,
      var(--color-accent-primary) 0%,
      var(--color-accent-secondary) 100%
    );
    border-radius: var(--radius-full);
  }
`;

const ContactText = styled.p`
  font-size: 1.125rem;
  color: var(--color-text-secondary);
  margin-bottom: var(--spacing-md);
  line-height: 1.8;
`;

const ContactForm = styled.form`
  background: linear-gradient(
    135deg,
    rgba(255, 255, 255, 0.05) 0%,
    rgba(255, 255, 255, 0.02) 100%
  );
  border-radius: var(--radius-lg);
  padding: var(--spacing-lg);
  backdrop-filter: blur(10px);
  border: 1px solid rgba(255, 255, 255, 0.05);
  
  @media (max-width: 768px) {
    order: 1;
  }
`;

const FormGroup = styled.div`
  margin-bottom: var(--spacing-md);
`;

const FormLabel = styled.label`
  display: block;
  margin-bottom: 0.5rem;
  color: var(--color-text-secondary);
  font-size: 0.875rem;
`;

const FormInput = styled.input`
  width: 100%;
  background-color: rgba(255, 255, 255, 0.05);
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: var(--radius-md);
  padding: 0.75rem 1rem;
  color: var(--color-text-primary);
  font-family: var(--font-body);
  font-size: 1rem;
  transition: all 0.3s ease;
  
  &:focus {
    outline: none;
    border-color: var(--color-accent-primary);
    background-color: rgba(255, 255, 255, 0.1);
  }
  
  &::placeholder {
    color: rgba(255, 255, 255, 0.3);
  }
`;

const FormTextarea = styled.textarea`
  width: 100%;
  background-color: rgba(255, 255, 255, 0.05);
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: var(--radius-md);
  padding: 0.75rem 1rem;
  color: var(--color-text-primary);
  font-family: var(--font-body);
  font-size: 1rem;
  min-height: 150px;
  resize: vertical;
  transition: all 0.3s ease;
  
  &:focus {
    outline: none;
    border-color: var(--color-accent-primary);
    background-color: rgba(255, 255, 255, 0.1);
  }
  
  &::placeholder {
    color: rgba(255, 255, 255, 0.3);
  }
`;

const FormButton = styled(motion.button)`
  background-color: var(--color-accent-primary);
  color: var(--color-bg-dark);
  border: none;
  padding: 0.75rem 1.5rem;
  border-radius: var(--radius-full);
  font-weight: 500;
  font-size: 1rem;
  letter-spacing: 1px;
  text-transform: uppercase;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
  margin-top: var(--spacing-sm);
  box-shadow: 0 4px 15px rgba(166, 140, 255, 0.25);
  transition: all var(--transition-fast);
  cursor: pointer;
  
  &:hover {
    background-color: var(--color-accent-secondary);
    transform: translateY(-2px);
    box-shadow: 0 6px 20px rgba(166, 140, 255, 0.4);
  }
`;

const Footer = styled.footer`
  padding: var(--spacing-lg) 0;
  background-color: var(--color-bg-medium);
  color: var(--color-text-secondary);
  border-top: 1px solid rgba(255, 255, 255, 0.05);
`;

const FooterContent = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  
  @media (max-width: 768px) {
    flex-direction: column;
    gap: var(--spacing-md);
    text-align: center;
  }
`;

const FooterLogo = styled.div`
  font-family: var(--font-heading);
  font-size: 1.5rem;
  font-weight: 600;
  color: var(--color-text-primary);
  
  span {
    color: var(--color-accent-primary);
  }
`;

const FooterCopyright = styled.p`
  font-size: 0.875rem;
  color: var(--color-text-tertiary);
`;

const MadeWithLove = styled.div`
  font-size: 0.875rem;
  color: var(--color-text-tertiary);
  margin-top: var(--spacing-xs);
  display: flex;
  align-items: center;
  justify-content: center;
  
  svg {
    color: var(--color-accent-tertiary);
    margin: 0 0.25rem;
  }
`;

const App = () => {
  const [loading, setLoading] = useState(true);
  
  useEffect(() => {
    // Smooth scrolling setup with Lenis
    const lenis = new Lenis({
      duration: 1.2,
      easing: (t) => Math.min(1, 1.001 - Math.pow(2, -10 * t)),
      direction: 'vertical',
      gestureDirection: 'vertical',
      smooth: true,
      mouseMultiplier: 1,
      smoothTouch: false,
      touchMultiplier: 2,
      infinite: false,
    });
    
    // Get scroll value for animations
    function raf(time) {
      lenis.raf(time);
      requestAnimationFrame(raf);
    }
    
    requestAnimationFrame(raf);
    
    // Simulate loading (in a real project, this would be based on actual asset loading)
    const loadingTimer = setTimeout(() => {
      setLoading(false);
    }, 2000);
    
    return () => {
      clearTimeout(loadingTimer);
      lenis.destroy();
    };
  }, []);
  
  return (
    <>
      <GlobalStyles />
      <Cursor />
      
      {/* Loading Screen */}
      {loading && (
        <LoadingScreen>
          <LoadingContent>
            <LogoText
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.7 }}
            >
              Shes<span>Psyched</span>
            </LogoText>
            
            <LoadingBar>
              <LoadingProgress
                animate={{
                  width: ["0%", "100%"],
                  transition: { duration: 1.5, ease: "easeInOut" }
                }}
              />
            </LoadingBar>
          </LoadingContent>
        </LoadingScreen>
      )}
      
      {/* Main Content */}
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ 
          opacity: loading ? 0 : 1,
          transition: { duration: 1, delay: 0.3 }
        }}
      >
        <HomeContainer>
          <Navigation />
          <ParallaxHero />
          <ExperienceSection />
          <AnimatedServices />
          <FeatureSpotlight />
          <TestimonialSlider />
          
          {/* Contact Section */}
          <ContactSection id="contact">
            <ContentContainer>
              <SectionHeading>
                <HeadingLabel
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.5 }}
                  viewport={{ once: false, amount: 0.2 }}
                >
                  Get in Touch
                </HeadingLabel>
                
                <SectionTitle
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.5, delay: 0.1 }}
                  viewport={{ once: false, amount: 0.2 }}
                >
                  Let's Connect
                </SectionTitle>
              </SectionHeading>
              
              <div style={{ maxWidth: "800px", margin: "0 auto" }}>
                <ContactText
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.5 }}
                  viewport={{ once: false, amount: 0.2 }}
                  style={{ textAlign: "center", marginBottom: "var(--spacing-lg)" }}
                >
                  I'm currently supporting students in the St. Vrain Valley School District. If you're interested in learning more about my services or would like to discuss potential opportunities, please reach out using the form below.
                </ContactText>
                
                <ContactForm>
                  <FormGroup>
                    <FormLabel>Name</FormLabel>
                    <FormInput type="text" placeholder="Your name" />
                  </FormGroup>
                  
                  <FormGroup>
                    <FormLabel>Email</FormLabel>
                    <FormInput type="email" placeholder="Your email address" />
                  </FormGroup>
                  
                  <FormGroup>
                    <FormLabel>School/Organization (Optional)</FormLabel>
                    <FormInput type="text" placeholder="Your school or organization" />
                  </FormGroup>
                  
                  <FormGroup>
                    <FormLabel>Message</FormLabel>
                    <FormTextarea placeholder="How can I help you?" />
                  </FormGroup>
                  
                  <FormButton
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                  >
                    Send Message
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <path d="M22 2L11 13" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                      <path d="M22 2L15 22L11 13L2 9L22 2Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                    </svg>
                  </FormButton>
                </ContactForm>
              </div>
            </ContentContainer>
          </ContactSection>
          
          {/* Footer */}
          <Footer>
            <ContentContainer>
              <FooterContent>
                <FooterLogo>
                  Shes<span>Psyched</span>
                </FooterLogo>
                
                <FooterCopyright>
                  © {new Date().getFullYear()} Laura St. Pierre. All rights reserved.
                </FooterCopyright>
              </FooterContent>
              
              <MadeWithLove>
                Made with 
                <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor" xmlns="http://www.w3.org/2000/svg">
                  <path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z" />
                </svg>
                for Laura
              </MadeWithLove>
            </ContentContainer>
          </Footer>
        </HomeContainer>
      </motion.div>
    </>
  );
};

export default App;